'use client';

import { HStack, VStack, Box } from '@terra/ui-styled-system';
import {
  IconButton,
  BodyDefault,
  LabelDefault,
  MonoSmall,
} from '@terra/ui-design-system';
import { Delete02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { CommentWithUser } from '@terra/shared-contracts-comments';
import { formatRelativeDate } from '../../utils/formatRelativeDate';
import { ProfileAvatar } from '../layout/profile/ProfileAvatar';

type CommentDisplayProps = {
  comment: CommentWithUser;
  onDelete: () => void;
  canDelete: boolean;
  variant?: 'inline' | 'stacked';
};

export function CommentDisplay({
  comment,
  onDelete,
  canDelete,
  variant = 'inline',
}: CommentDisplayProps) {
  const t = useTranslations('layout');
  const user = comment.createdByUser;
  const displayName = user?.name || 'Unknown User';
  const deleteCommentLabel = t('deleteComment');
  const picture = user?.picture || undefined;

  if (variant === 'stacked') {
    return (
      <VStack
        gap="2"
        alignItems="flex-start"
        p="3"
        borderRadius="md"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="border.neutral.muted"
        bg="bg.neutral.subtle"
        w="full"
      >
        <HStack gap="2" alignItems="center" w="full">
          <ProfileAvatar name={displayName} picture={picture} size="sm" />
          <VStack gap="0" alignItems="flex-start" flex="1" minW="0">
            <BodyDefault truncate>{displayName}</BodyDefault>
            <LabelDefault color="fg.neutral.muted">
              {formatRelativeDate(comment.createdAt)}
            </LabelDefault>
          </VStack>
          {canDelete ? (
            <IconButton
              variant="ghost"
              size="sm"
              onClick={onDelete}
              aria-label={deleteCommentLabel}
            >
              <Delete02Icon size={14} />
            </IconButton>
          ) : null}
        </HStack>
        <BodyDefault color="fg.neutral.muted" whiteSpace="pre-wrap">
          {comment.content}
        </BodyDefault>
      </VStack>
    );
  }

  return (
    <VStack gap="1" alignItems="flex-start" w="full">
      <HStack gap="1" alignItems="center" w="full">
        <ProfileAvatar name={displayName} picture={picture} size="xs" />
        <HStack gap="1" alignItems="center" minW="0" flex="1" overflow="hidden">
          <BodyDefault truncate color="fg.neutral.default">
            {displayName}
          </BodyDefault>
          <MonoSmall color="fg.neutral.subtle" flexShrink={0}>
            {formatRelativeDate(comment.createdAt)}
          </MonoSmall>
        </HStack>
        {canDelete ? (
          <IconButton
            variant="ghost"
            size="xs"
            onClick={onDelete}
            aria-label={deleteCommentLabel}
            flexShrink={0}
          >
            <Delete02Icon size={14} />
          </IconButton>
        ) : null}
      </HStack>
      <Box pl="7" w="full">
        <BodyDefault color="fg.neutral.muted" whiteSpace="pre-wrap">
          {comment.content}
        </BodyDefault>
      </Box>
    </VStack>
  );
}
